/**
 * Hooks and helpers backing the SKU catalog.
 * Kept out of SkuPicker.tsx so the component file is component-only
 * (react-refresh rule).
 */
import { useQuery } from "@tanstack/react-query";
import { getSkus, type Sku } from "@/api/skus";

export function useSkuCatalog() {
  return useQuery({
    queryKey: ["skus"],
    queryFn: () => getSkus(),
    staleTime: 5 * 60 * 1000,
  });
}

export function lookupSku(
  skus: Sku[] | undefined,
  code: string | null,
): Sku | undefined {
  if (!code || !skus) return undefined;
  return skus.find((s) => s.code === code);
}

// Dash-family code points folded to "-" before trimming. Mirrors the backend
// `canonical_sku_code` (sku_codes.py) so the UI can preview the stored code
// and flag obvious duplicates before submitting (rev03 r3-4). The backend
// remains authoritative — this is a best-effort hint, not a guarantee.
const SKU_DASHES = /[-‐‑‒–—―⁃−﹘﹣－]/g;

export function canonicalSkuCode(raw: string): string {
  const folded = raw.normalize("NFKC").replace(SKU_DASHES, "-");
  const collapsed = folded.replace(/\s+/g, " ");
  // Trim leading/trailing spaces and hyphens (appended visual noise).
  return collapsed.replace(/^[\s-]+/, "").replace(/[\s-]+$/, "");
}
